import React from 'react';
import {
  CheckCircle2, ArrowRight, Lock, Sparkles,
  ClipboardList, Target, Loader2, X
} from 'lucide-react';

/**
 * ProgressGapsView
 * ----------------
 * Shows participants — in plain, kindergarten-clear language — exactly which
 * tasks/quizzes they still have to complete before the next module unlocks.
 *
 * Used in two places:
 *  1. Modal popup when clicking a locked module in the course overview.
 *  2. Full-page view inside <PDLLearningWrapper> when opening a locked module.
 *
 * Design principles:
 *  - ONE flat numbered step list (no nested per-module groupings).
 *  - First open step is a hero "Dein nächster Schritt" card with one big CTA.
 *  - Friendly, encouraging language. No jargon.
 *  - Each step is one click away from the actual task / quiz.
 */

const STEP_KIND = {
  QUIZ: 'quiz',
  TASK: 'task',
  FALLBEISPIEL: 'fallbeispiel',
};

// Turn the API "gaps" array (grouped by module) into a flat list of single steps.
const flattenGaps = (gaps) => {
  const steps = [];
  (gaps || []).forEach((g) => {
    if (!g.quiz_passed) {
      steps.push({
        kind: STEP_KIND.QUIZ,
        moduleId: g.module_id,
        moduleOrder: g.module_order,
        moduleTitle: g.module_title,
        title: `Quiz für Modul ${g.module_order}`,
        attempted: g.quiz_attempted,
        score: Math.round(g.quiz_score || 0),
      });
    }
    (g.missing_tasks || []).forEach((t) => {
      steps.push({
        kind: STEP_KIND.TASK,
        moduleId: g.module_id,
        moduleOrder: g.module_order,
        moduleTitle: g.module_title,
        taskId: t.id,
        title: t.title,
      });
    });
    if (g.fallbeispiele_required && !g.fallbeispiele_done) {
      steps.push({
        kind: STEP_KIND.FALLBEISPIEL,
        moduleId: g.module_id,
        moduleOrder: g.module_order,
        moduleTitle: g.module_title,
        title: `Fallbeispiele für Modul ${g.module_order}`,
      });
    }
  });
  // Sort by module order so steps appear in study order
  return steps.sort((a, b) => a.moduleOrder - b.moduleOrder);
};

const StepIcon = ({ kind, className = '' }) => {
  if (kind === STEP_KIND.QUIZ) {
    return <Target className={className} strokeWidth={1.75} />;
  }
  return <ClipboardList className={className} strokeWidth={1.75} />;
};

const stepLabel = (kind) => (kind === STEP_KIND.QUIZ ? 'Quiz' : kind === STEP_KIND.FALLBEISPIEL ? 'Fallbeispiele' : 'Aufgabe');

const ProgressGapsView = ({
  loading = false,
  error = false,
  gaps = [],
  lockedModuleOrder,
  lockedModuleTitle,
  // Optional: total counts of already-done items across previous modules
  // (purely informational, used for the progress bar). If not provided we
  // estimate based on gaps.
  doneCount,
  totalCount,
  onNavigateToModule, // (moduleId, target: 'tasks' | 'quiz') => void
  onClose,            // optional — shown in modal
  variant = 'page',   // 'page' | 'modal'
}) => {
  const steps = flattenGaps(gaps);
  const missingCount = steps.length;

  // Progress bar values — if explicit counts not given, estimate from gaps
  const requiredSum = (gaps || []).reduce(
    (s, g) => s + (g.required_task_count || 0) + 1 /* +1 for quiz */,
    0
  );
  const submittedSum = (gaps || []).reduce(
    (s, g) => s + (g.submitted_task_count || 0) + (g.quiz_passed ? 1 : 0),
    0
  );
  const total = totalCount ?? requiredSum;
  const done = doneCount ?? submittedSum;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 100;

  // ----- LOADING -----
  if (loading) {
    return (
      <div className="flex items-center justify-center py-14 text-sand-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Wir schauen kurz nach, was noch fehlt …</span>
      </div>
    );
  }

  // ----- ERROR -----
  if (error) {
    return (
      <div className="text-coral-dark text-sm py-10 text-center">
        Status konnte gerade nicht geladen werden. Bitte später erneut versuchen.
      </div>
    );
  }

  // ----- ALL DONE -----
  if (missingCount === 0) {
    return (
      <div className="text-center py-10 px-4" data-testid="gaps-all-done">
        <div className="w-16 h-16 rounded-full bg-sage/15 mx-auto mb-4 flex items-center justify-center">
          <CheckCircle2 className="h-9 w-9 text-sage" strokeWidth={1.6} />
        </div>
        <h3 className="font-display text-2xl text-espresso mb-2">Super gemacht!</h3>
        <p className="text-sand-700 text-sm leading-relaxed max-w-md mx-auto">
          Alle vorherigen Module sind komplett.
          {lockedModuleOrder && (
            <> Modul <strong>{lockedModuleOrder}</strong> wird gleich
              automatisch freigeschaltet.</>
          )}
        </p>
        <p className="text-sand-500 text-xs mt-3">
          Falls es länger dauert, einfach kurz die Seite neu laden — oder die
          Kursleitung kontaktieren.
        </p>
      </div>
    );
  }

  // ----- MAIN LIST -----
  const [next, ...rest] = steps;

  return (
    <div className="space-y-5" data-testid="gaps-step-list">
      {/* HERO summary */}
      <div className="bg-espresso text-cream-50 rounded-3xl p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-cream-50/10 flex items-center justify-center flex-shrink-0">
            <Lock className="w-6 h-6 text-peach-200" />
          </div>
          <div className="flex-1 min-w-0">
            {variant === 'modal' && lockedModuleOrder && (
              <div className="text-[11px] uppercase tracking-[0.22em] text-peach-200 font-semibold mb-1">
                Modul {lockedModuleOrder} ist noch zu
              </div>
            )}
            <h2 className="font-display text-xl sm:text-2xl leading-tight tracking-wide mb-2">
              Du musst noch {missingCount === 1 ? 'eine Sache' : <><strong>{missingCount}</strong> Sachen</>} erledigen
              {lockedModuleOrder && <> — dann öffnet sich Modul {lockedModuleOrder}!</>}
            </h2>
            <p className="text-cream-200/80 text-sm leading-relaxed">
              Mach die Schritte einfach der Reihe nach. Wenn alle erledigt sind,
              schaltet sich das nächste Modul ganz automatisch frei.
            </p>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs text-cream-200/80">Dein Fortschritt</span>
                <span className="text-sm font-semibold">
                  {done} <span className="text-cream-200/70 font-normal">von {total} erledigt · {pct}%</span>
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-cream-50/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-peach-200 to-peach-dark transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>

          {variant === 'modal' && onClose && (
            <button
              onClick={onClose}
              className="text-cream-200 hover:text-cream-50 transition-colors p-1 flex-shrink-0"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* DEIN NÄCHSTER SCHRITT — hero card for the very first missing item */}
      <div>
        <div className="flex items-center gap-2 mb-2 ml-1">
          <Sparkles className="w-4 h-4 text-terra" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-terra font-bold">
            Dein nächster Schritt
          </span>
        </div>
        <button
          onClick={() => onNavigateToModule?.(next.moduleId, next.kind === STEP_KIND.QUIZ ? 'quiz' : 'tasks')}
          className="w-full text-left bg-peach-50 hover:bg-peach-100 border-2 border-peach-200 hover:border-peach-dark rounded-3xl p-5 sm:p-6 transition-all group shadow-soft"
          data-testid="gaps-next-step"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-cream-50 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <StepIcon kind={next.kind} className="w-7 h-7 text-terra" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-sand-600 font-semibold mb-1">
                Schritt 1 von {missingCount}
                <span className="mx-1.5 text-sand-400">·</span>
                aus Modul {next.moduleOrder}: {next.moduleTitle}
              </div>
              <h3 className="font-display text-lg sm:text-xl text-espresso leading-snug mb-2">
                {next.kind === STEP_KIND.QUIZ ? (
                  <>{next.attempted ? 'Quiz noch einmal versuchen' : 'Quiz machen'} <span className="text-terra">→</span></>
                ) : (
                  <>Aufgabe machen: <span className="italic">{next.title}</span></>
                )}
              </h3>
              {next.kind === STEP_KIND.QUIZ && next.attempted && (
                <p className="text-sand-700 text-sm mb-2">
                  Letzter Versuch: <strong>{next.score}%</strong> — du brauchst mindestens
                  <strong> 70%</strong>, um zu bestehen.
                </p>
              )}
              <div className="inline-flex items-center gap-2 mt-1 px-5 py-2.5 rounded-full bg-espresso text-cream-50 font-medium text-sm group-hover:bg-terra-dark transition-colors">
                Jetzt erledigen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Restliche Schritte */}
      {rest.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-sand-600 font-bold mb-2 ml-1">
            Danach noch …
          </div>
          <ol className="space-y-2.5">
            {rest.map((s, idx) => (
              <li key={`${s.kind}-${s.moduleId}-${s.taskId || 'quiz'}`}>
                <button
                  onClick={() => onNavigateToModule?.(s.moduleId, s.kind === STEP_KIND.QUIZ ? 'quiz' : 'tasks')}
                  className="w-full text-left bg-cream-50 hover:bg-peach-50 border border-cream-300 hover:border-peach-200 rounded-2xl p-4 transition-all group flex items-center gap-4"
                  data-testid={`gaps-step-${idx + 2}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-cream-200 flex items-center justify-center flex-shrink-0 group-hover:bg-peach-200 transition-colors">
                    <span className="font-display text-sand-700 text-base">{idx + 2}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <StepIcon kind={s.kind} className="w-3.5 h-3.5 text-sand-500" />
                      <span className="text-[11px] uppercase tracking-wider text-sand-500 font-semibold">
                        {stepLabel(s.kind)} · Modul {s.moduleOrder}
                      </span>
                    </div>
                    <div className="text-sand-800 text-sm font-medium leading-snug truncate">
                      {s.kind === STEP_KIND.QUIZ ? (
                        <>Quiz {s.attempted ? `nochmal versuchen (${s.score}%)` : 'machen'}</>
                      ) : (
                        s.title
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all flex-shrink-0" />
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Friendly footer */}
      <div className="bg-cream-100 border border-cream-300 rounded-2xl px-5 py-4 text-center text-xs text-sand-600">
        <span className="font-semibold text-sand-800">Du schaffst das!</span> Sobald du
        alle Schritte erledigt hast, geht das nächste Modul ganz von alleine auf.
      </div>
    </div>
  );
};

export default ProgressGapsView;
